import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Apple, 
  Plus, 
  Trash2, 
  Edit, 
  Loader2, 
  Users,
  CheckCircle2,
  Lock,
  Unlock,
  BookOpen,
  Calculator,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Eye
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIAS = [
  { value: 'FUNDAMENTOS', label: 'Fundamentos' },
  { value: 'MACROS', label: 'Macros' },
  { value: 'PRACTICA', label: 'Práctica' },
  { value: 'AVANZADO', label: 'Avanzado' },
];

const ModuloNutricionPage = () => {
  const [activeTab, setActiveTab] = useState('contenido');
  const [pildoras, setPildoras] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [expandedPildora, setExpandedPildora] = useState(null);
  
  const [formData, setFormData] = useState({
    numero_orden: '',
    titulo: '',
    contenido: '',
    categoria: 'FUNDAMENTOS',
    video_url: '',
    recursos: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pildorasRes, clientesRes] = await Promise.all([
        axios.get(`${API}/admin/nutricion/pildoras`),
        axios.get(`${API}/admin/nutricion/clientes-con-acceso`)
      ]);
      setPildoras(pildorasRes.data.pildoras || []);
      setClientes(clientesRes.data.clientes || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedInicial = async () => {
    setSeeding(true);
    try {
      const response = await axios.post(`${API}/admin/nutricion/seed-inicial`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Error al crear contenido inicial');
    } finally {
      setSeeding(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.numero_orden || !formData.titulo || !formData.contenido) {
      toast.error('Completa todos los campos obligatorios');
      return;
    }

    setSubmitting(true);
    try {
      const data = {
        numero_orden: parseInt(formData.numero_orden),
        titulo: formData.titulo,
        contenido: formData.contenido,
        categoria: formData.categoria,
        video_url: formData.video_url || null,
        recursos: formData.recursos || null
      };

      if (editing) {
        await axios.put(`${API}/admin/nutricion/pildoras/${editing.id}`, data);
        toast.success('Píldora actualizada');
      } else {
        await axios.post(`${API}/admin/nutricion/pildoras`, data);
        toast.success('Píldora creada');
      }
      
      setShowDialog(false);
      setEditing(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta píldora?')) return;
    
    try {
      await axios.delete(`${API}/admin/nutricion/pildoras/${id}`);
      toast.success('Píldora eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleEdit = (pildora) => {
    setEditing(pildora);
    setFormData({
      numero_orden: pildora.numero_orden.toString(),
      titulo: pildora.titulo,
      contenido: pildora.contenido,
      categoria: pildora.categoria,
      video_url: pildora.video_url || '',
      recursos: pildora.recursos || ''
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      numero_orden: pildoras.length > 0 ? (Math.max(...pildoras.map(p => p.numero_orden)) + 1).toString() : '1',
      titulo: '',
      contenido: '',
      categoria: 'FUNDAMENTOS',
      video_url: '',
      recursos: ''
    });
  };

  const openNewDialog = () => {
    setEditing(null);
    resetForm();
    setShowDialog(true);
  };

  const getCategoriaColor = (cat) => {
    switch (cat) {
      case 'FUNDAMENTOS': return 'bg-blue-500/20 text-blue-400';
      case 'MACROS': return 'bg-green-500/20 text-green-400';
      case 'PRACTICA': return 'bg-amber-500/20 text-amber-400';
      case 'AVANZADO': return 'bg-purple-500/20 text-purple-400';
      default: return 'bg-zinc-500/20 text-zinc-400';
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-3">
              <Apple className="h-7 w-7 text-green-400" />
              Módulo de Nutrición
            </h1>
            <p className="text-muted-foreground mt-1">
              Contenido educativo de nutrición (oculto para clientes por defecto)
            </p>
          </div>
          <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 text-sm">
            <Eye className="h-4 w-4" />
            Solo visible para ti
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-400">{pildoras.length}</p>
              <p className="text-sm text-muted-foreground">Píldoras</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-400">{clientes.length}</p>
              <p className="text-sm text-muted-foreground">Con acceso</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-purple-400">
                {clientes.filter(c => c.herramientas_desbloqueadas).length}
              </p>
              <p className="text-sm text-muted-foreground">Completado</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-amber-400">29€</p>
              <p className="text-sm text-muted-foreground">Precio sugerido</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid grid-cols-2 gap-1 bg-card border border-border p-1 h-auto max-w-md">
            <TabsTrigger 
              value="contenido"
              className="flex items-center gap-2 data-[state=active]:bg-green-500/20 data-[state=active]:text-green-400"
            >
              <BookOpen className="h-4 w-4" />
              Contenido
            </TabsTrigger>
            <TabsTrigger 
              value="clientes"
              className="flex items-center gap-2 data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400"
            >
              <Users className="h-4 w-4" />
              Clientes
            </TabsTrigger>
          </TabsList>

          {/* TAB: CONTENIDO */}
          <TabsContent value="contenido" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <BookOpen className="h-5 w-5 text-green-400" />
                    Píldoras de Nutrición
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleSeedInicial}
                      disabled={seeding}
                      variant="outline"
                      className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                    >
                      {seeding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                      {pildoras.length === 0 ? 'Crear Contenido Científico' : 'Actualizar a Científico 2025'}
                    </Button>
                    <Button onClick={openNewDialog} className="bg-green-600 hover:bg-green-700">
                      <Plus className="h-4 w-4 mr-2" />
                      Nueva Píldora
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {pildoras.length === 0 ? (
                  <div className="p-8 bg-zinc-800/50 rounded-lg text-center">
                    <Apple className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">No hay contenido creado</p>
                    <p className="text-xs text-muted-foreground mt-1">Haz clic en "Crear Contenido Inicial" para empezar</p>
                  </div>
                ) : (
                  pildoras.map((pildora) => {
                    const isExpanded = expandedPildora === pildora.id;
                    return (
                      <div key={pildora.id} className="bg-zinc-800/50 border border-zinc-700 rounded-lg overflow-hidden">
                        <div 
                          className="p-4 flex items-center gap-4 cursor-pointer hover:bg-zinc-800/80 transition-colors"
                          onClick={() => setExpandedPildora(isExpanded ? null : pildora.id)}
                        >
                          <div className="w-10 h-10 flex items-center justify-center bg-green-500/20 text-green-400 rounded-lg font-bold">
                            {pildora.numero_orden}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${getCategoriaColor(pildora.categoria)}`}>
                                {pildora.categoria}
                              </span>
                            </div>
                            <h4 className="font-semibold text-foreground">{pildora.titulo}</h4>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleEdit(pildora); }}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="text-destructive" onClick={(e) => { e.stopPropagation(); handleDelete(pildora.id); }}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                            {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                          </div>
                        </div>
                        
                        {isExpanded && (
                          <div className="px-4 pb-4 border-t border-zinc-700 pt-4">
                            <div className="prose prose-invert prose-sm max-w-none pl-14">
                              {pildora.contenido.split('\n\n').map((p, i) => (
                                <p key={i} className="text-sm text-foreground/90 mb-3 whitespace-pre-line">
                                  {p.split('**').map((part, j) => 
                                    j % 2 === 1 ? <strong key={j} className="text-green-300">{part}</strong> : part
                                  )}
                                </p>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB: CLIENTES */}
          <TabsContent value="clientes" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Users className="h-5 w-5 text-blue-400" />
                  Clientes con Acceso
                </CardTitle>
              </CardHeader>
              <CardContent>
                {clientes.length === 0 ? (
                  <div className="p-8 bg-zinc-800/50 rounded-lg text-center">
                    <Lock className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">Ningún cliente tiene acceso todavía</p>
                    <p className="text-xs text-muted-foreground mt-1">Ve al detalle de un cliente y dale acceso al módulo</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {clientes.map((cliente) => (
                      <div key={cliente.id} className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${cliente.herramientas_desbloqueadas ? 'bg-green-500/20' : 'bg-blue-500/20'}`}>
                            {cliente.herramientas_desbloqueadas ? (
                              <CheckCircle2 className="h-5 w-5 text-green-400" />
                            ) : (
                              <BookOpen className="h-5 w-5 text-blue-400" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{cliente.name}</p>
                            <p className="text-xs text-muted-foreground">{cliente.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-foreground">
                            Píldora {cliente.pildora_actual} de {pildoras.length}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {cliente.herramientas_desbloqueadas ? '✅ Herramientas desbloqueadas' : `${cliente.completadas} completadas`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Dialog crear/editar */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Apple className="h-5 w-5 text-green-400" />
                {editing ? 'Editar Píldora' : 'Nueva Píldora de Nutrición'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Número de orden *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.numero_orden}
                    onChange={(e) => setFormData({ ...formData, numero_orden: e.target.value })}
                    className="bg-zinc-900/50 border-zinc-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Categoría</Label>
                  <Select value={formData.categoria} onValueChange={(v) => setFormData({ ...formData, categoria: v })}>
                    <SelectTrigger className="bg-zinc-900/50 border-zinc-800">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIAS.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Título *</Label>
                <Input
                  value={formData.titulo}
                  onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                  placeholder="Ej: ¿Qué son las calorías?"
                  className="bg-zinc-900/50 border-zinc-800"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Contenido * (Markdown)</Label>
                <Textarea
                  value={formData.contenido}
                  onChange={(e) => setFormData({ ...formData, contenido: e.target.value })}
                  placeholder="Escribe el contenido educativo..."
                  className="bg-zinc-900/50 border-zinc-800 min-h-[250px] font-mono text-sm"
                />
              </div>
              
              <div className="space-y-2">
                <Label>URL de Video (opcional)</Label>
                <Input
                  value={formData.video_url}
                  onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                  placeholder="https://..."
                  className="bg-zinc-900/50 border-zinc-800"
                />
              </div>
            </div>
            
            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
              <Button onClick={handleSubmit} disabled={submitting} className="bg-green-600 hover:bg-green-700">
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editing ? 'Guardar' : 'Crear Píldora'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default ModuloNutricionPage;
